import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import Logo from "@/shared/components/branding/Logo";
import AsyncButton from "@/shared/components/feedback/AsyncButton";
import { useAuth } from "../../../lib/auth";
import { isMockApi } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { vendor } = await login(email.trim(), password);
      const displayName = vendor?.name || vendor?.email;
      toast.success(`Welcome back, ${displayName}`);
      if (vendor?.onboarded) nav("/dashboard");
      else nav("/onboarding");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between gap-4">
            <Logo />
            <Link to="/" className="shrink-0 text-sm text-ink-secondary hover:text-ink-primary" data-testid="login-back-home">
              Back to home
            </Link>
          </div>
          <h1 className="mt-10 text-3xl font-display font-medium tracking-tight" data-testid="login-title">
            Welcome back
          </h1>
          <p className="mt-2 text-ink-secondary">Sign in to manage your storefront.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className="input-field mt-2"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="password">Password</label>
                {/* Hidden under the mock adapter, where there is no server to
                    reset against — a link to a "not available" screen is worse
                    than no link. See ResetPasswordPage. */}
                {!isMockApi && (
                  <Link
                    to={`/reset-password${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
                    className="text-xs text-brand hover:text-brand-hover transition-colors"
                    data-testid="login-forgot-password"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative mt-2">
                <input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field pr-11"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-ink-muted hover:text-ink-primary transition-colors"
                  aria-label="Toggle password visibility"
                  data-testid="login-password-toggle"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <AsyncButton
              type="submit"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-btn"
              pending={busy}
              pendingLabel="Signing in…"
            >
              <>Sign in <ArrowRight className="h-4 w-4" /></>
            </AsyncButton>
          </form>

          <p className="mt-8 text-sm text-ink-secondary">
            New here?{" "}
            <Link to="/register" className="text-brand hover:text-brand-hover font-medium transition-colors" data-testid="login-goto-register">
              Create an account
            </Link>
          </p>
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:block relative overflow-hidden border-l border-line">
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand/20 via-transparent to-restaurant/10" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/30504703/pexels-photo-30504703.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <blockquote className="max-w-md">
            <p className="text-2xl font-display font-medium leading-snug">
              "Digital Indian Market turned our WhatsApp menu into a real storefront overnight."
            </p>
            <footer className="mt-4 text-ink-secondary text-sm">— Rohan M., Owner · Pizza Hub</footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
